(() => {
  'use strict';
  if (window.CabaneFiltersAdapter) return;

  const overlayToggle = 'dialog.cs [data-cs-filters]';
  const listingToggle = '[data-cabane-filter-toggle], summary.mobile-facets__open-wrapper, [data-dialog="cm-filters"]';
  const rollerCollections = new Set(['rollers-en-ligne', 'rollers-enfant', 'rollers-balade-fitness',
    'rollers-balade-1', 'rollers-quad', 'rollers-derby', 'rollers-freestyle-agressif',
    'rollers-3-roues', 'rollers-vitesse-course', 'rollers-hockey', 'rollers-hockey-1']);
  const english = () => document.documentElement.lang.toLowerCase().startsWith('en');
  const isFilter = key => key.startsWith('filter.');
  const isCore = (key,value) => key === 'filter.p.vendor' && value.toLowerCase() === 'core';
  const listing = url => /\/(?:collections\/[^/]+|search)\/?$/.test(url.pathname);

  // Present the merchant's CORE / Core vendor aliases as one filter throughout the UI.
  const filtersOnly = params => {
    const out = new URLSearchParams();
    for (let [key,value] of params) {
      if (!isFilter(key) || !value) continue;
      if (isCore(key,value)) value = 'Core';
      if (!out.getAll(key).includes(value)) out.append(key,value);
    }
    return out;
  };
  function filterURL(base, params) {
    const url = new URL(base);
    for (const key of new Set([...url.searchParams.keys()])) {
      if (isFilter(key) || key === 'page' || key === 'section_id' || key === 'sections') url.searchParams.delete(key);
    }
    for (const [key,value] of filtersOnly(params)) {
      url.searchParams.append(key,value);
      if (isCore(key,value)) url.searchParams.append(key,'CORE');
    }
    return url;
  }
  function triggerFor(target) {
    const opener = target instanceof Element && target.closest(overlayToggle + ', ' + listingToggle + ', [data-cb-filter-open]');
    if (!opener) return null;
    if (opener.matches('[data-cb-filter-open]')) return window.CabaneBuilderFilterAdapter ? opener : null;
    if (opener.matches(overlayToggle)) return opener.closest('dialog.cs[open]') ? opener : null;
    return listing(new URL(location.href)) && opener.closest('#MainContent') ? opener : null;
  }
  function context(opener) {
    if (!triggerFor(opener)) return null;
    if (opener.matches('[data-cb-filter-open]')) return window.CabaneBuilderFilterAdapter.context(opener);
    const overlay = opener.closest('dialog.cs');
    let base, title;
    if (overlay) {
      const form = opener.closest('form');
      if (!form) return null;
      base = new URL(form.action || overlay.dataset.searchUrl, location.href);
      if (base.origin !== location.origin || !listing(base)) return null;
      base.search = new URLSearchParams(new FormData(form)).toString();
      for (const key of new Set([...base.searchParams.keys()])) {
        if (base.searchParams.getAll(key).every(value => !value)) base.searchParams.delete(key);
      }
      title = english() ? 'Search results' : 'Résultats de recherche';
    } else {
      base = new URL(location.href);
      const isSearch = /\/search\/?$/.test(base.pathname);
      const heading = document.querySelector('#MainContent h1');
      title = isSearch ? (english() ? 'Search results' : 'Résultats de recherche') :
        heading?.textContent.replace(/\s+/g,' ').trim() || (english() ? 'Products' : 'Produits');
    }
    const collection = base.pathname.match(/\/collections\/([^/]+)\/?$/)?.[1];
    const discipline = rollerCollections.has(collection) ? 'roller' :
      base.searchParams.get('univers') === 'scooter' || /^trottinettes?-/.test(collection || '') ? 'scooter' :
      /\/search\/?$/.test(base.pathname) ? 'search' : 'catalog';
    let subset;
    if (!overlay && collection) {
      const term = base.searchParams.get('cabane_q')?.trim() || '';
      const choice = base.searchParams.get('cabane_selection');
      let ids;
      if (choice) {
        try {
          const config = JSON.parse(document.querySelector('[data-scooter-choice-products]')?.textContent || '{}');
          if (Array.isArray(config[choice])) ids = new Set(config[choice]);
        } catch { /* Missing optional guidance data has the same effect as no matching choice. */ }
      }
      const sale = !!document.querySelector('[data-cabane-sale-only="true"]');
      if (sale || term || ids) subset = {sale,term,ids};
    }
    return {kind:overlay ? 'overlay' : 'catalog', title, discipline, params:filtersOnly(base.searchParams), base, trigger:opener, overlay, subset};
  }
  function decodeSource(root, ctx, url) {
    const countRaw = root?.getAttribute('data-count');
    if (!/^\d+$/.test(countRaw || '')) throw new Error('Filter section is unavailable.');
    const facets = Array.from(root.querySelectorAll('[data-cf-group]'), group => {
      const {key,label,kind} = group.dataset;
      const facet = {key,label,type:kind,values:[]};
      if (kind === 'price') {
        const number = val => val == null || val === '' ? null : Number(val);
        return {...facet,minKey:group.dataset.minKey,maxKey:group.dataset.maxKey,min:number(group.dataset.min),
          max:number(group.dataset.max),limit:number(group.dataset.limit)};
      }
      let core;
      for (const entry of group.querySelectorAll('[data-cf-value]')) {
        const value = entry.dataset.value, count = Number(entry.dataset.count), active = entry.hasAttribute('data-active');
        if (!Number.isFinite(count)) continue;
        if (isCore(key,value)) {
          if (!core) {
            core = {value:'Core',label:'CORE',count:0,active:false,disabled:true};
            facet.values.push(core);
          }
          core.count += count;
          core.active ||= active;
          core.disabled = !core.count && !core.active;
        } else {
          facet.values.push({value,label:entry.dataset.label,count,active,disabled:!count && !active,...(kind === 'color' ? {color:entry.dataset.color || null} : {})});
        }
      }
      return facet;
    });
    return {count:Number(countRaw),facets,title:ctx.title,universe:ctx.discipline,url:url.href,currency:root.dataset.currency};
  }

  const cache = new Map();
  const fold = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g,'').toLowerCase();
  async function applySubsetCounts(ctx, url, data, signal) {
    const {sale,term,ids} = ctx.subset;
    const tokens = fold(term).split(/\s+/).filter(Boolean);
    const index = new URL(url);
    for (const key of ['section_id','sections','cabane_q','cabane_selection','cabane_page','page']) index.searchParams.delete(key);
    index.searchParams.set('view','cabane-search-index');
    let count = 0, pages = 1;
    for (let page = 1; page <= pages; page++) {
      index.searchParams.set('page',String(page));
      const res = await fetch(index,{signal, credentials:'same-origin'});
      if (!res.ok) throw new Error('Filtered collection is unavailable.');
      const json = await res.json();
      if (!Array.isArray(json.items) || !Number.isInteger(json.pages) || json.pages < 0) throw new Error('Invalid collection index.');
      pages = json.pages;
      for (const product of json.items) {
        if (sale && !product.sale || ids && !ids.has(product.id)) continue;
        if (tokens.length) {
          const terms = fold([product.title,product.vendor,product.type,...product.tags || []].join(' '));
          if (!tokens.every(token => terms.includes(token))) continue;
        }
        count++;
      }
    }
    data.count = count;
    // Per-choice Shopify counts apply to the parent collection, not the local
    // text/promotion/curation subset. Only show the exact total in this context.
    for (const facet of data.facets) for (const value of facet.values) delete value.count;
  }
  async function query(ctx, params, signal) {
    if (ctx.kind === 'builder') return window.CabaneBuilderFilterAdapter.query(ctx,params,signal);
    if (signal?.aborted) throw new DOMException('Aborted','AbortError');
    const url = filterURL(ctx.base,params);
    const key = url.href;
    const hit = cache.get(key);
    if (hit && Date.now() - hit.at < 15000) return hit.payload;
    const endpoint = new URL(url);
    endpoint.searchParams.set('section_id','cabane-filter-source');
    const res = await fetch(endpoint, {signal,credentials:'same-origin',headers:{Accept:'text/html'}});
    if (!res.ok) throw new Error('Filter request failed.');
    const doc = new DOMParser().parseFromString(await res.text(),'text/html');
    const payload = decodeSource(doc.querySelector('[data-cf-source]'),ctx,url);
    if (ctx.subset) await applySubsetCounts(ctx,url,payload,signal);
    cache.set(key,{at:Date.now(),payload});
    if (cache.size > 50) cache.delete(cache.keys().next().value);
    return payload;
  }

  async function applyOverlay(ctx, url, data, signal) {
    const results = ctx.overlay?.querySelector('[data-cs-results]');
    const previous = results?.querySelector('[data-cs-response]');
    if (!ctx.overlay?.open || !results) throw new Error('Search closed.');
    const form = previous?.querySelector('form') || document.createElement('form');
    const temporary = !form.isConnected;
    if (temporary) { form.hidden = true; results.prepend(form); }
    // The official search overlay owns its results, history, sorting and pagination.
    // Feed its existing submit handler exactly the new request, even when an unchecked
    // facet has vanished from the current result set and has no native field left.
    const transfer = event => {
      for (const key of new Set([...event.formData.keys()])) event.formData.delete(key);
      for (const [key,value] of url.searchParams) event.formData.append(key,value);
    };
    const core = form.querySelector('[data-cs-core-values]');
    const before = core?.checked;
    return new Promise((resolve,reject) => {
      const finish = error => {
        observer.disconnect();
        clearTimeout(timeout);
        signal?.removeEventListener('abort',abort);
        ctx.overlay.removeEventListener('close',abort);
        if (error) reject(error); else resolve();
      };
      const abort = () => finish(new DOMException('Search closed','AbortError'));
      const observer = new MutationObserver(() => {
        if (results.getAttribute('aria-busy') === 'true') return;
        const next = results.querySelector('[data-cs-response]');
        if (next && next === previous) return;
        const nextQuery = next?.querySelector('input[name=q]')?.value;
        const loaded = next && (nextQuery || null) === url.searchParams.get('q') && Number(next.dataset.count) === data.count;
        finish(loaded ? null : new Error('Search did not apply the requested filters.'));
      });
      const timeout = setTimeout(() => finish(new Error('Search timed out.')),18000);
      observer.observe(results,{childList:true, attributes:true,attributeFilter:['aria-busy']});
      signal?.addEventListener('abort',abort,{once:true});
      ctx.overlay.addEventListener('close',abort,{once:true});
      // CORE aliases are already expanded in the transfer, irrespective of which
      // spelling the older form contains after the preceding search.
      if (core) core.checked = false;
      form.addEventListener('formdata',transfer,{once:true});
      try {
        form.dispatchEvent(new Event('submit',{bubbles:true,cancelable:true}));
      } catch (error) {
        finish(error);
      } finally {
        form.removeEventListener('formdata',transfer);
        if (core) core.checked = before;
        if (temporary) form.remove();
      }
    });
  }
  async function apply(ctx, params, data, signal) {
    if (ctx.kind === 'builder') return window.CabaneBuilderFilterAdapter.apply(ctx,params,data,signal);
    if (signal?.aborted) throw new DOMException('Aborted','AbortError');
    const url = filterURL(ctx.base,params);
    if (ctx.kind === 'overlay') {
      if (filterURL(ctx.base,ctx.params).href !== url.href) await applyOverlay(ctx,url,data,signal);
      return;
    }
    if (location.pathname !== ctx.base.pathname) throw new Error('Catalog changed.');
    url.searchParams.delete('cabane_page');
    if (ctx.subset && window.CabaneCatalogSearch?.handleFilters(url.searchParams.toString(),null,true)) return;
    if (window.CabaneCatalogNavigation?.navigate) {
      await window.CabaneCatalogNavigation.navigate(url.href);
    } else if (typeof FacetFiltersForm !== 'undefined' && typeof FacetFiltersForm.renderPage === 'function' &&
               document.querySelector('#ProductGridContainer')) {
      FacetFiltersForm.renderPage(url.searchParams.toString(),null);
    } else {
      location.assign(url.href);
    }
  }
  function focus(ctx) {
    if (ctx.kind === 'builder') return window.CabaneBuilderFilterAdapter.focus(ctx);
    const target = ctx.trigger?.isConnected ? ctx.trigger : ctx.kind === 'overlay' ?
      ctx.overlay?.querySelector('[data-cs-filters]') : document.querySelector('[data-cabane-filter-toggle], #MainContent .mobile-facets__open-wrapper');
    target?.focus({preventScroll:true});
    return target;
  }
  window.CabaneFiltersAdapter = {triggerFor,context,query,apply,focus};
})();
